import type { FieldDecision, ImportRecord } from '@app/src/types/tabularImport'
import { parseExtractedData } from '@app/src/types/tabularImport'
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import React, { useMemo, useState } from 'react'
import { getFieldLabel, isReadOnlyField } from '../fieldConfig'
import { applyBulkDecision, buildInitialDecisions, getFieldStatus } from '../compareDecisions'
import { useTabularImportEntity } from '../TabularImportEntityContext'

interface FieldCompareViewProps {
  importRecord: ImportRecord
  matchedEntitySnapshot: Record<string, string | null>
  fieldDecisions: Record<string, FieldDecision> | null
  onSave: (fieldDecisions: Record<string, FieldDecision>) => void
  onClose: () => void
}

const FieldCompareView: React.FC<FieldCompareViewProps> = ({
  importRecord,
  matchedEntitySnapshot,
  fieldDecisions,
  onSave,
  onClose,
}) => {
  const { fieldConfigs } = useTabularImportEntity()

  const extractedFields = useMemo(
    () => parseExtractedData(importRecord.extractedData)?.extractedFields ?? {},
    [importRecord.extractedData],
  )

  const allKeys = useMemo(() => {
    const presentKeys = new Set([...Object.keys(extractedFields), ...Object.keys(matchedEntitySnapshot)])
    // Config-defined fields first (in config order), then any extra keys alphabetically
    const configKeys = fieldConfigs.map(f => f.key).filter(k => presentKeys.has(k))
    const configKeySet = new Set(configKeys)
    const extraKeys = [...presentKeys].filter(k => !configKeySet.has(k)).sort()
    return [...configKeys, ...extraKeys]
  }, [extractedFields, matchedEntitySnapshot, fieldConfigs])

  const [decisions, setDecisions] = useState<Record<string, FieldDecision>>(() =>
    buildInitialDecisions(fieldConfigs, extractedFields, matchedEntitySnapshot, fieldDecisions),
  )

  const handleDecisionChange = (key: string, value: FieldDecision | null) => {
    if (value === null) return
    setDecisions(prev => ({ ...prev, [key]: value }))
  }

  const applyToAll = (target: FieldDecision) =>
    setDecisions(applyBulkDecision(fieldConfigs, allKeys, extractedFields, matchedEntitySnapshot, decisions, target))

  return (
    <Paper variant="outlined" sx={{ bgcolor: 'action.hover', p: 1.5 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="body2" color="text.secondary">
          Field-level merge decisions for row {importRecord.rowNumber}
        </Typography>
        <Box display="flex" gap={1}>
          <Button size="small" variant="text" onClick={() => applyToAll('import')}>
            Use all import values
          </Button>
          <Button size="small" variant="text" onClick={() => applyToAll('keep')}>
            Keep all existing
          </Button>
        </Box>
      </Box>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                <Typography variant="body2" fontWeight={600}>
                  Field
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" fontWeight={600}>
                  Import Value
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" fontWeight={600}>
                  Existing Value
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" fontWeight={600}>
                  Decision
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {allKeys.map(key => {
              const importVal = extractedFields[key] ?? null
              const existingVal = matchedEntitySnapshot[key] ?? null
              const status = getFieldStatus(importVal, existingVal)
              const readOnly = isReadOnlyField(fieldConfigs, key)

              return (
                <TableRow key={key}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {getFieldLabel(fieldConfigs, key)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      color={
                        readOnly
                          ? 'text.disabled'
                          : status === 'differs'
                            ? 'warning.dark'
                            : importVal
                              ? 'text.primary'
                              : 'text.disabled'
                      }
                    >
                      {importVal || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {existingVal || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {readOnly ? (
                      <Typography variant="body2" color="text.secondary">
                        Keep existing
                      </Typography>
                    ) : status === 'same' ? (
                      <Typography variant="body2" color="success.main">
                        Same value
                      </Typography>
                    ) : status === 'import_empty' ? (
                      <Typography variant="body2" color="text.secondary">
                        Keep existing
                      </Typography>
                    ) : key === 'Owners' ? (
                      <ToggleButtonGroup
                        exclusive
                        size="small"
                        value={decisions[key] ?? 'add'}
                        onChange={(_, value) => handleDecisionChange(key, value)}
                      >
                        <ToggleButton value="add">Add to existing</ToggleButton>
                        <ToggleButton value="import">Replace</ToggleButton>
                        <ToggleButton value="keep">Keep existing</ToggleButton>
                      </ToggleButtonGroup>
                    ) : status === 'existing_empty' ? (
                      <Typography variant="body2" color="warning.dark">
                        Use import
                      </Typography>
                    ) : (
                      <ToggleButtonGroup
                        exclusive
                        size="small"
                        value={decisions[key] ?? 'keep'}
                        onChange={(_, value) => handleDecisionChange(key, value)}
                      >
                        <ToggleButton value="import">Use import</ToggleButton>
                        <ToggleButton value="keep">Keep existing</ToggleButton>
                      </ToggleButtonGroup>
                    )}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Box display="flex" justifyContent="flex-end" gap={1} mt={1.5}>
        <Button size="small" variant="text" onClick={onClose}>
          Cancel
        </Button>
        <Button size="small" variant="contained" onClick={() => onSave(decisions)}>
          Save decisions
        </Button>
      </Box>
    </Paper>
  )
}

export default FieldCompareView
